import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  ShieldCheck, Upload, CreditCard, Building, Award, CheckCircle, 
  FileText, Lock, ChevronRight, Download, Printer, User, Phone, Mail, FileCode
} from 'lucide-react';

const inputStyle = {
  width: '100%',
  padding: '0.65rem 0.85rem',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box' as const,
  backgroundColor: '#ffffff'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: '700',
  color: '#334155',
  marginBottom: '0.35rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px'
};

const pakCities = [
  'Quetta', 'Karachi', 'Lahore', 'Islamabad', 'Peshawar', 'Rawalpindi', 
  'Multan', 'Faisalabad', 'Hyderabad', 'Sukur', 'Gwadar', 'Sibi', 'Turbat', 'Abbottabad'
];

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    cnic: '',
    cnicExpiry: '',
    phone: '',
    email: '',
    password: '',
    role: 'client',
    
    // Practitioner & Judge fields
    registrationNumber: '',
    registrationExpiry: '',
    
    // Law Firm fields
    officeAddress: '',
    cityName: 'Quetta',
    districtName: 'Quetta District',
    headOfficeLocation: '',
    subOffices: '',
    selectedMemberPractitioners: [] as string[],
    
    // Bank Account Payout details
    bankAccountTitle: '',
    bankName: '',
    iban: '',

    // Terms
    termsAccepted: false,
    termsVersion: 'v3.0',

    // Subscription & Payment
    subscriptionType: 'trial', // 'trial' | 'subscription'
    selectedPlan: 'Standard Practitioner',
    paymentMethod: 'JazzCash',
    transactionRef: '',
  });

  const [cnicFrontFile, setCnicFrontFile] = useState<File | null>(null);
  const [cnicBackFile, setCnicBackFile] = useState<File | null>(null);
  const [regCardFile, setRegCardFile] = useState<File | null>(null);

  const [step, setStep] = useState(1); // 1: Registration Form, 2: Payment/Plan Gateway, 3: Receipt & Pending Approval Notice
  const [paymentReceipt, setPaymentReceipt] = useState<any | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // CNIC Auto-Formatting helper (XXXXX-XXXXXXX-X)
  const formatCNIC = (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 13);
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 12) return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(12)}`;
  };

  const handleCNICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNIC(e.target.value);
    setFormData({ ...formData, cnic: formatted });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData({ ...formData, [name]: val });
  };

  const isJudge = formData.role === 'district_judge' || formData.role === 'honourable_justice';

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.termsAccepted) {
      setError('You must accept the Terms & Conditions to proceed.');
      return;
    }

    if (isJudge) {
      // Honorable Judges are 100% exempt from subscription and bank info
      submitRegistration('EXEMPT_JUDGE_FREE', 0);
      return;
    }

    // Proceed to Step 2: Payment Plan Gateway
    setStep(2);
  };

  const handlePaymentProcess = () => {
    if (formData.subscriptionType === 'subscription' && !formData.transactionRef) {
      setError('Please enter your payment deposit Transaction Reference ID / Slip No.');
      return;
    }

    const amount = formData.subscriptionType === 'trial' ? 0 : 
      formData.selectedPlan === 'Basic Client' ? 5000 : 
      formData.selectedPlan === 'Standard Practitioner' ? 15000 : 
      formData.selectedPlan === 'Corporate Law Firm' ? 35000 : 20000;

    const receipt = {
      receiptNo: `ELUM-TXN-${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric' }),
      time: new Date().toLocaleTimeString(),
      subscriberName: formData.name,
      cnic: formData.cnic,
      role: formData.role.toUpperCase(),
      plan: formData.subscriptionType === 'trial' ? '30-Days Free Trial' : formData.selectedPlan,
      paymentMethod: formData.paymentMethod,
      transactionRef: formData.subscriptionType === 'trial' ? 'TRIAL-GRACE-PERIOD' : (formData.transactionRef || `TXN-${Date.now()}`),
      amountPaid: amount,
      status: 'SUCCESSFUL (PENDING ADMIN APPROVAL)',
    };

    setPaymentReceipt(receipt);
    submitRegistration(receipt.transactionRef, receipt.amountPaid);
  };

  const submitRegistration = async (txnRef: string, amountPaid: number) => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        transactionRef: txnRef,
        amountPaid: amountPaid,
        approvalStatus: 'pending_approval', // Superuser approval required
        cnicFrontUploaded: !!cnicFrontFile,
        cnicBackUploaded: !!cnicBackFile,
        regCardUploaded: !!regCardFile,
      };

      const response = await api.post('/auth/register', payload);
      setStep(3); // Receipt & Approval Confirmation
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration request submitted. Superuser approval pending.');
      setStep(3); // Show confirmation receipt
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#090d16', fontFamily: '"Outfit", sans-serif', color: '#f8fafc' }}>
      
      {/* HEADER */}
      <header style={{ padding: '1rem 2rem', backgroundColor: '#0b1120', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <Link to="/" style={{ color: 'white', textDecoration: 'none', fontWeight: '800', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src="/logo_ELum_Firm4.png" alt="Logo" style={{ height: '36px', width: '36px', borderRadius: '50%', border: '1.5px solid #ecc94b', objectFit: 'contain' }} />
          <div>
            <span>ELUM LAW ASSOCIATES</span>
            <span style={{ fontSize: '0.72rem', color: '#ecc94b', display: 'block', fontWeight: '700' }}>Multi-Role Registration Portal</span>
          </div>
        </Link>
        <Link to="/" style={{ color: '#ecc94b', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 'bold' }}>
          ← Back to Main Portal
        </Link>
      </header>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem 1.5rem' }}>
        
        {/* STEP 1: FORM INPUTS */}
        {step === 1 && (
          <form onSubmit={handleFormSubmit} style={{ padding: '2rem', backgroundColor: '#0f172a', borderRadius: '16px', border: '1px solid #1e293b', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', width: '100%', maxWidth: '850px' }}>
            
            <div style={{ borderBottom: '1px solid #1e293b', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#ffffff', margin: '0 0 0.35rem 0', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <ShieldCheck color="#ecc94b" size={28} /> User Registration & Identity Verification
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                Complete your professional profile. Bar Council Registrations and Client CNICs are verified prior to superuser approval.
              </p>
            </div>

            {error && (
              <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', color: '#fca5a5', padding: '0.85rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.88rem' }}>
                ⚠️ {error}
              </div>
            )}

            {/* SECTION 1: PRIMARY BIOGRAPHICS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
              
              <div>
                <label style={labelStyle}>Full Legal Name *</label>
                <input name="name" type="text" value={formData.name} onChange={handleChange} required style={inputStyle} placeholder="e.g. Barrister NajeebUllah Khan" />
              </div>

              <div>
                <label style={labelStyle}>Designated Role *</label>
                <select name="role" value={formData.role} onChange={handleChange} style={inputStyle}>
                  <option value="client">👤 Client (Litigant)</option>
                  <option value="practitioner">⚖️ Practitioner (Advocate / Lawyer)</option>
                  <option value="law_firm">🏢 Law Firm (Corporate / Chambers)</option>
                  <option value="district_judge">🏛️ District Judge</option>
                  <option value="honourable_justice">⚖️ Honourable Justice (High Court / SC)</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>CNIC Number (Auto-Dash Format) *</label>
                <input name="cnic" type="text" value={formData.cnic} onChange={handleCNICChange} required style={inputStyle} placeholder="XXXXX-XXXXXXX-X" maxLength={15} />
              </div>

              <div>
                <label style={labelStyle}>CNIC Expiry Date *</label>
                <input name="cnicExpiry" type="date" value={formData.cnicExpiry} onChange={handleChange} required style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Phone Number *</label>
                <input name="phone" type="text" value={formData.phone} onChange={handleChange} required style={inputStyle} placeholder="0300-1234567" />
              </div>

              <div>
                <label style={labelStyle}>Email ID *</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} required style={inputStyle} placeholder="name@elumlaw.com" />
              </div>

              <div>
                <label style={labelStyle}>Password *</label>
                <input name="password" type="password" value={formData.password} onChange={handleChange} required style={inputStyle} placeholder="••••••••" />
              </div>

            </div>

            {/* SECTION 2: ROLE-SPECIFIC UPLOADS & BAR COUNCIL DETAILS */}
            {formData.role === 'client' && (
              <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#ecc94b', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                  🪪 Client Identity Documents (CNIC Copy Front & Back Upload)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>CNIC Front Copy (.JPG, .PDF)</label>
                    <input type="file" accept="image/*,.pdf" onChange={(e) => setCnicFrontFile(e.target.files?.[0] || null)} style={{ color: '#94a3b8', fontSize: '0.85rem' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>CNIC Back Copy (.JPG, .PDF)</label>
                    <input type="file" accept="image/*,.pdf" onChange={(e) => setCnicBackFile(e.target.files?.[0] || null)} style={{ color: '#94a3b8', fontSize: '0.85rem' }} />
                  </div>
                </div>
              </div>
            )}

            {(formData.role === 'practitioner' || formData.role === 'law_firm' || isJudge) && (
              <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#38bdf8', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                  📜 Bar Council / Judicial Registration Credentials & Card Upload
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Registration / License Number *</label>
                    <input name="registrationNumber" type="text" value={formData.registrationNumber} onChange={handleChange} required={!isJudge} style={inputStyle} placeholder="e.g. BC-PBC-89412 / BHC-904" />
                  </div>
                  <div>
                    <label style={labelStyle}>Registration Expiry Date *</label>
                    <input name="registrationExpiry" type="date" value={formData.registrationExpiry} onChange={handleChange} required={!isJudge} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Upload Registration Card (.PDF, .JPG)</label>
                    <input type="file" accept="image/*,.pdf" onChange={(e) => setRegCardFile(e.target.files?.[0] || null)} style={{ color: '#94a3b8', fontSize: '0.85rem' }} />
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 3: LAW FIRM LOCATIONS & AFFILIATES */}
            {formData.role === 'law_firm' && (
              <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#4ade80', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                  🏢 Law Firm Multi-Location & Head Office Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Head Office Location Address *</label>
                    <input name="headOfficeLocation" type="text" value={formData.headOfficeLocation} onChange={handleChange} required style={inputStyle} placeholder="e.g. Suite 402, High Court Bar Chambers, Quetta" />
                  </div>
                  <div>
                    <label style={labelStyle}>Base City Name *</label>
                    <select name="cityName" value={formData.cityName} onChange={handleChange} style={inputStyle}>
                      {pakCities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>District Jurisdiction *</label>
                    <input name="districtName" type="text" value={formData.districtName} onChange={handleChange} style={inputStyle} placeholder="e.g. Quetta District" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Sub-Offices / Branch Locations (Optional)</label>
                  <input name="subOffices" type="text" value={formData.subOffices} onChange={handleChange} style={inputStyle} placeholder="e.g. Branch 1: Karachi Shahrah-e-Faisal; Branch 2: Lahore Gulberg" />
                </div>
              </div>
            )}

            {/* SECTION 4: BANK ACCOUNT & IBAN FOR PAYOUTS */}
            {(formData.role === 'practitioner' || formData.role === 'law_firm') && (
              <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.25rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#c084fc', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                  🏦 Bank Account & IBAN Details for Service Share Payouts
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Account Title *</label>
                    <input name="bankAccountTitle" type="text" value={formData.bankAccountTitle} onChange={handleChange} required style={inputStyle} placeholder="e.g. NajeebUllah Law Associates" />
                  </div>
                  <div>
                    <label style={labelStyle}>Bank Name *</label>
                    <input name="bankName" type="text" value={formData.bankName} onChange={handleChange} required style={inputStyle} placeholder="e.g. Habib Bank Limited (HBL)" />
                  </div>
                  <div>
                    <label style={labelStyle}>IBAN Number (24 Characters) *</label>
                    <input name="iban" type="text" value={formData.iban} onChange={handleChange} required style={inputStyle} placeholder="PK36HABB0001234567890102" maxLength={24} />
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 5: JUDGE FREE EXEMPTION NOTICE */}
            {isJudge && (
              <div style={{ backgroundColor: 'rgba(236,201,75,0.1)', border: '1px solid #ecc94b', padding: '1rem', borderRadius: '8px', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#fef08a' }}>
                ⚖️ <strong>Honourable Judicial Exemption Notice:</strong> District Judges and High Court / Supreme Court Justices are 100% exempt from subscription fees and bank information. Access is completely free, restricted strictly to <strong>Elum Law Dictionary</strong>, <strong>Elum Law Directory</strong>, and <strong>Live Judicial AI Chat</strong>.
              </div>
            )}

            {/* SECTION 6: TERMS ACCEPTANCE */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input name="termsAccepted" type="checkbox" checked={formData.termsAccepted} onChange={handleChange} required id="terms" style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              <label htmlFor="terms" style={{ fontSize: '0.85rem', color: '#cbd5e1', cursor: 'pointer' }}>
                I accept the <Link to="/terms" style={{ color: '#ecc94b' }}>Terms & Conditions (v3.0)</Link> and confirm all uploaded credential documents are authentic.
              </label>
            </div>

            <button 
              type="submit" 
              style={{ width: '100%', padding: '0.85rem', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '800', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
            >
              {isJudge ? 'Complete Judicial Free Registration' : 'Proceed to Subscription & Payment Gateways'} <ChevronRight size={18} />
            </button>

          </form>
        )}

        {/* STEP 2: SUBSCRIPTION OPTIONS & PAYMENT DEPOSIT GATEWAY */}
        {step === 2 && (
          <div style={{ padding: '2rem', backgroundColor: '#0f172a', borderRadius: '16px', border: '1px solid #1e293b', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', width: '100%', maxWidth: '850px' }}>
            
            <div style={{ borderBottom: '1px solid #1e293b', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#ffffff', margin: '0 0 0.35rem 0' }}>
                💳 Subscription Selection & Deposit Gateway
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                Choose your subscription tier or opt for the 30-Days Free Trial before submitting for superuser approval.
              </p>
            </div>

            {/* CHOICE 1: 30-DAYS TRIAL VS SUBSCRIPTION */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div 
                onClick={() => setFormData({ ...formData, subscriptionType: 'trial' })}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '2px solid',
                  borderColor: formData.subscriptionType === 'trial' ? '#38bdf8' : '#1e293b',
                  backgroundColor: formData.subscriptionType === 'trial' ? 'rgba(56,189,248,0.1)' : '#070b12',
                  cursor: 'pointer'
                }}
              >
                <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#38bdf8', margin: '0 0 0.4rem 0' }}>🎁 30-Days Free Trial</h4>
                <p style={{ fontSize: '0.8rem', color: '#cbd5e1', margin: 0 }}>Instant 30-day full access to platform features with zero initial deposit required.</p>
              </div>

              <div 
                onClick={() => setFormData({ ...formData, subscriptionType: 'subscription' })}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '2px solid',
                  borderColor: formData.subscriptionType === 'subscription' ? '#ecc94b' : '#1e293b',
                  backgroundColor: formData.subscriptionType === 'subscription' ? 'rgba(236,201,75,0.1)' : '#070b12',
                  cursor: 'pointer'
                }}
              >
                <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#ecc94b', margin: '0 0 0.4rem 0' }}>⭐ Subscribe Now (Full License)</h4>
                <p style={{ fontSize: '0.8rem', color: '#cbd5e1', margin: 0 }}>Select a annual/monthly plan with priority referral dispatch and verified badge.</p>
              </div>
            </div>

            {/* SUBSCRIPTION PLANS PANEL */}
            {formData.subscriptionType === 'subscription' && (
              <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: '800', color: '#ffffff', marginBottom: '0.85rem', textTransform: 'uppercase' }}>Select Subscription Plan:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                  {[
                    { name: 'Basic Client', price: 'PKR 5,000 / yr', desc: 'Case tracking, AI Assistant & directory access' },
                    { name: 'Standard Practitioner', price: 'PKR 15,000 / yr', desc: 'Direct case routing, payouts & citation engine' },
                    { name: 'Corporate Law Firm', price: 'PKR 35,000 / yr', desc: 'Multi-branch sub-offices & affiliate member pool' }
                  ].map(p => (
                    <div 
                      key={p.name}
                      onClick={() => setFormData({ ...formData, selectedPlan: p.name })}
                      style={{
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: formData.selectedPlan === p.name ? '#ecc94b' : '#334155',
                        backgroundColor: formData.selectedPlan === p.name ? 'rgba(236,201,75,0.15)' : '#0f172a',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontSize: '0.9rem', fontWeight: '800', color: 'white' }}>{p.name}</div>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#ecc94b', margin: '0.2rem 0' }}>{p.price}</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{p.desc}</div>
                    </div>
                  ))}
                </div>

                {/* PAYMENT DEPOSIT METHOD SELECTION */}
                <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #1e293b' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: '800', color: '#38bdf8', marginBottom: '0.75rem', textTransform: 'uppercase' }}>Depositing Payment Method:</h4>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    {['AuroraWallet', 'EasyPaisa', 'JazzCash', 'IBAN Bank Transfer'].map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setFormData({ ...formData, paymentMethod: m })}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: formData.paymentMethod === m ? '#38bdf8' : '#334155',
                          backgroundColor: formData.paymentMethod === m ? '#38bdf8' : '#1e293b',
                          color: formData.paymentMethod === m ? '#090d16' : '#cbd5e1',
                          fontWeight: '800',
                          fontSize: '0.82rem',
                          cursor: 'pointer'
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  <label style={labelStyle}>Deposit Transaction Reference ID / Slip No. *</label>
                  <input 
                    name="transactionRef" 
                    type="text" 
                    value={formData.transactionRef} 
                    onChange={handleChange} 
                    required 
                    style={inputStyle} 
                    placeholder="e.g. JZ-984120349 / HBL-TXN-77140" 
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button 
                type="button"
                onClick={() => setStep(1)}
                style={{ padding: '0.8rem 1.5rem', borderRadius: '8px', border: '1px solid #334155', backgroundColor: 'transparent', color: '#94a3b8', fontWeight: '700', cursor: 'pointer' }}
              >
                ← Back
              </button>
              <button 
                type="button"
                onClick={handlePaymentProcess}
                disabled={loading}
                style={{ flex: 1, padding: '0.8rem 1.5rem', borderRadius: '8px', border: 'none', backgroundColor: '#ecc94b', color: '#090d16', fontWeight: '800', fontSize: '1rem', cursor: 'pointer' }}
              >
                {loading ? 'Submitting Registration...' : 'Complete & Submit for Superuser Approval'}
              </button>
            </div>

          </div>
        )}

        {/* STEP 3: TRANSACTION RECEIPT & SUPERUSER APPROVAL CONFIRMATION */}
        {step === 3 && (
          <div style={{ padding: '2rem', backgroundColor: '#0f172a', borderRadius: '16px', border: '1px solid #1e293b', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', width: '100%', maxWidth: '700px', textAlign: 'center' }}>
            
            <div style={{ display: 'inline-flex', padding: '1rem', borderRadius: '50%', backgroundColor: 'rgba(74, 222, 128, 0.15)', color: '#4ade80', marginBottom: '1rem' }}>
              <CheckCircle size={48} />
            </div>

            <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#ffffff', margin: '0 0 0.5rem 0' }}>
              Registration Submitted Successfully!
            </h2>
            <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginBottom: '1.5rem' }}>
              Your application has been logged into the Database Administrator / Superuser queue for final verification and role activation.
            </p>

            {/* PRINTABLE TRANSACTION RECEIPT */}
            <div style={{ backgroundColor: '#ffffff', color: '#0f172a', padding: '1.75rem', borderRadius: '12px', textAlign: 'left', marginBottom: '1.5rem', border: '2px solid #ecc94b', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #0f172a', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>ELUM LAW ASSOCIATES</h3>
                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '700' }}>Registration Deposit Receipt</span>
                </div>
                <span style={{ backgroundColor: '#0f172a', color: '#ecc94b', padding: '0.3rem 0.65rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800' }}>
                  {paymentReceipt?.receiptNo || 'ELUM-TXN-984102'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
                <div>Subscriber Name: <strong>{formData.name}</strong></div>
                <div>Role: <strong>{formData.role.toUpperCase()}</strong></div>
                <div>CNIC #: <strong>{formData.cnic}</strong></div>
                <div>Phone #: <strong>{formData.phone}</strong></div>
                <div>Package / Plan: <strong>{paymentReceipt?.plan || '30-Days Free Trial'}</strong></div>
                <div>Deposit Gateway: <strong>{formData.paymentMethod}</strong></div>
                <div>Transaction Ref: <strong>{paymentReceipt?.transactionRef || 'TRIAL-GRACE'}</strong></div>
                <div>Amount Paid: <strong>PKR {paymentReceipt?.amountPaid?.toLocaleString() || 0}</strong></div>
              </div>

              <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '0.75rem', fontSize: '0.72rem', color: '#64748b', textAlign: 'center' }}>
                © Elum Law Associates — Supreme Court & High Court Chambers Pakistan. All Rights Reserved (E&OE).
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={handlePrintReceipt}
                style={{ padding: '0.65rem 1.25rem', borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <Printer size={16} /> Print Receipt
              </button>
              <button 
                onClick={() => navigate('/login')}
                style={{ padding: '0.65rem 1.75rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', fontWeight: '800', fontSize: '0.88rem', cursor: 'pointer' }}
              >
                Proceed to Login →
              </button>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default RegisterPage;
